/** Page-level help content for each editor section. */
export interface PageHelp {
  title: string;
  description: string;
  tips: string[];
}

export const pageHelp: Record<string, PageHelp> = {
  hero: {
    title: 'Homepage Banner',
    description: 'This is the first thing visitors see — a big banner at the top of your homepage. Make it count with a strong headline and a great photo.',
    tips: [
      'Keep your headline short and punchy — 5-8 words is ideal.',
      'Use a high-resolution photo (at least 1600px wide). The image fills the full width of the screen.',
      'The button is optional. If you add one, make the text action-oriented ("Shop Now", "See My Work").',
    ],
  },
  about: {
    title: 'About Section',
    description: 'Your About section tells visitors who you are. It appears on the homepage and has its own page too.',
    tips: [
      'Write in first person — "I" feels more personal than "we" for solo creators.',
      'A square photo works best for the profile image.',
      'Keep it genuine. People connect with real stories, not marketing speak.',
    ],
  },
  product: {
    title: 'Product',
    description: 'Add the details for this product. It\'ll appear on your Products page, and on the homepage if you mark it as Featured.',
    tips: [
      'The first image is the main photo shown in product listings.',
      'For the price, type it exactly how you want it shown (e.g., "$850" or "POA").',
      'Use categories to help visitors filter — they appear as tabs on your products page.',
    ],
  },
  products: {
    title: 'Products',
    description: 'Your product catalogue. Each product gets its own card on the Products page. Featured products also appear on your homepage.',
    tips: [
      'Add categories first (in the Categories section), then assign them to products.',
      'Mark your best items as "Featured" so they show on the homepage.',
      'Reorder products by editing the sort order on each one.',
    ],
  },
  gallery: {
    title: 'Gallery',
    description: 'A visual showcase of your work. The gallery shows on its own page with a masonry-style layout.',
    tips: [
      'Use high-quality images — they\'re displayed large.',
      'Captions are optional but help with context.',
      'Mix different orientations (landscape and portrait) for visual interest.',
    ],
  },
  contact: {
    title: 'Contact',
    description: 'How people can reach you. This info appears on the Contact page.',
    tips: [
      'You don\'t need to fill everything — only add the channels you actually use.',
      'The contact form (if enabled) lets visitors send you messages via your site.',
      'Use full URLs for social links (e.g., https://instagram.com/you).',
    ],
  },
  site: {
    title: 'Site Settings',
    description: 'Settings that affect your whole site — section headings, which pages to show or hide, SEO settings, and other global options.',
    tips: [
      'Toggle sections on/off to control which pages appear in your navigation.',
      'Heading overrides let you rename sections to match your business.',
      'Fill in the SEO Title and Description for better Google search results.',
      'Upload a Social Sharing Image so your site looks great when shared on social media.',
      'Changes here apply site-wide after you publish.',
    ],
  },
  post: {
    title: 'Blog Post',
    description: 'Write a blog post or news update. Published posts appear on your Blog page.',
    tips: [
      'The slug is the URL for this post (e.g., "my-first-post" → yoursite.com/blog/my-first-post).',
      'Write a short excerpt — it\'s what people see in the blog listing before they click.',
      'Keep "Published" off while you\'re still writing (it won\'t appear on your live site).',
    ],
  },
  blog: {
    title: 'Blog',
    description: 'Your blog posts and news updates. Each post gets its own page on your site.',
    tips: [
      'Remember to turn on "Show Blog Section" in Site Settings for the blog to appear.',
      'Posts with a featured image look better in the listing view.',
      'Set a publish date so posts appear in chronological order.',
    ],
  },
  category: {
    title: 'Category',
    description: 'Categories help visitors browse your products. They appear as filter tabs on the Products page.',
    tips: [
      'Keep category names short (1-2 words).',
      'Use sort order to control which categories appear first.',
    ],
  },
  categories: {
    title: 'Categories',
    description: 'Product categories that appear as filter tabs on your Products page.',
    tips: [
      'Create categories here, then assign them to individual products.',
      'Lower sort order numbers appear first.',
    ],
  },
  menu: {
    title: 'Menu',
    description: 'Your food/drink menu. Items are grouped by category and shown on the Menu page.',
    tips: [
      'Remember to turn on "Show Menu Page" in Site Settings.',
      'Items with the same category name are grouped together automatically.',
      'Dietary tags (V, GF, DF, VG) show as small badges next to each item.',
      'Turn "Available" off for items that are temporarily unavailable — they\'ll be hidden.',
    ],
  },
  menuItem: {
    title: 'Menu Item',
    description: 'Details for this menu item. It\'ll appear on your Menu page under its category.',
    tips: [
      'Type the price exactly as you want it displayed (e.g., "$18.50" or "Market Price").',
      'Photos are optional but make the menu more appealing.',
      'Dietary tags: V = Vegetarian, VG = Vegan, GF = Gluten Free, DF = Dairy Free.',
    ],
  },
  testimonial: {
    title: 'Testimonial',
    description: 'A customer review or testimonial. Featured testimonials appear on your homepage.',
    tips: [
      'Use real quotes from real customers — authenticity builds trust.',
      'A rating of 5 stars is the default if left blank.',
      'Adding a source (like "Google Review") adds credibility.',
      'Turn on "Show on Homepage" for your best reviews.',
    ],
  },
  testimonials: {
    title: 'Testimonials',
    description: 'Customer reviews and testimonials. Featured ones appear on your homepage to build trust with new visitors.',
    tips: [
      'Remember to turn on "Show Testimonials Section" in Site Settings.',
      'Mark your strongest reviews as "Featured" so they show on the homepage.',
      'Mix reviews from different sources (Google, Yelp, in-person) for variety.',
    ],
  },
  hours: {
    title: 'Hours Entry',
    description: 'Set the opening and closing time for a specific day. Mark days as closed when you\'re not open.',
    tips: [
      'Type times exactly as you want them shown (e.g., "9:00 AM", "11:30 AM").',
      'Turn on "Closed This Day" for days you\'re not open — the times will be ignored.',
      'You can add entries for holidays or special days too (e.g., "Christmas Day").',
    ],
  },
  hoursList: {
    title: 'Opening Hours',
    description: 'Your business opening hours. These appear on your site so visitors know when to visit.',
    tips: [
      'Remember to turn on "Show Opening Hours" in Site Settings.',
      'Add one entry per day of the week, in order (Monday through Sunday).',
      'Use the reorder arrows to arrange days in the order you prefer.',
    ],
  },
};
